import { useContext, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Paper,
    TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { useFilterContext } from '../../usageandbilling/FilterContext';
import { DataContext } from '../../contexts/DataProvider';
import { useAlert } from '../../functions/useAlert';
import { fetchOrganisations } from '../../services/organisations.service';
import { fetchOrgUsers } from '../../services/users.service';

interface Organization {
    _id: string;
    name: string;
}

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
}

const GlobalFilterBar = () => {
    const {
        selectedOrgIds,
        selectedUserIds,
        dateRange,
        setSelectedOrgIds,
        setSelectedUserIds,
        setDateRange,
    } = useFilterContext();

    const { login } = useContext(DataContext)!;
    const [setAlert] = useAlert();

    // OrgMultiSelect state
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [orgsLoading, setOrgsLoading] = useState(false);

    // UserMultiSelect state
    const [users, setUsers] = useState<User[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Export state
    const [exportingCsv, setExportingCsv] = useState(false);

    const isGlobalAdmin = login?.data?.role === 'global admin';

    // Fetch organisations on mount
    useEffect(() => {
        const fetchOrgs = async () => {
            setOrgsLoading(true);
            try {
                const res = await fetchOrganisations();
                const list = Array.isArray(res)
                    ? (res as Organization[])
                    : ((res as any)?.organisations as Organization[]) || [];
                setOrgs(list);
            } catch (err) {
                console.error('Failed to fetch organisations:', err);
            } finally {
                setOrgsLoading(false);
            }
        };
        fetchOrgs();
    }, []);

    // Fetch users when selectedOrgIds changes
    useEffect(() => {
        if (selectedOrgIds.length === 0) {
            setUsers([]);
            setSelectedUserIds([]);
            return;
        }
        const fetchUsers = async () => {
            setUsersLoading(true);
            try {
                const res = await fetchOrgUsers(selectedOrgIds.join(','));
                setUsers(Array.isArray(res) ? (res as User[]) : []);
            } catch (err) {
                console.error('Failed to fetch users:', err);
                setUsers([]);
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();
    }, [selectedOrgIds]);

    const handleOrgChange = (_: unknown, newOrgs: Organization[]) => {
        setSelectedOrgIds(newOrgs.map((o) => o._id));
        setSelectedUserIds([]);
    };

    const handleUserChange = (_: unknown, newUsers: User[]) => {
        setSelectedUserIds(newUsers.map((u) => u._id));
    };

    const handleExportCsv = async () => {
        if (!dateRange[0] || !dateRange[1]) return;
        setExportingCsv(true);
        try {
            const baseURL =
                process.env.NODE_ENV === 'production'
                    ? '/api'
                    : 'http://localhost:4000/api';

            const params = new URLSearchParams();
            if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
            if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));
            params.set('startDate', dateRange[0].format('YYYY-MM-DD'));
            params.set('endDate', dateRange[1].format('YYYY-MM-DD'));

            const response = await fetch(`${baseURL}/dashboard/export-csv?${params}`, {
                headers: {
                    Accept: 'text/csv',
                    token: localStorage.getItem('token') || '',
                },
            });

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            let orgLabel: string;
            if (selectedOrgIds.length === 0) {
                orgLabel = 'All-Organizations';
            } else if (selectedOrgIds.length === 1) {
                const found = orgs.find((o) => o._id === selectedOrgIds[0]);
                orgLabel = found ? found.name.replace(/[^a-zA-Z0-9-]/g, '-') : 'Unknown';
            } else {
                orgLabel = 'MultiOrg';
            }

            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');
            link.download = `user-activity-${orgLabel}-${startDate}-to-${endDate}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setAlert('CSV exported successfully', 'success');
        } catch (err) {
            console.error('Failed to export CSV:', err);
            setAlert('Failed to export CSV', 'error');
        } finally {
            setExportingCsv(false);
        }
    };

    const selectedOrgObjects = orgs.filter((o) => selectedOrgIds.includes(o._id));
    const selectedUserObjects = users.filter((u) => selectedUserIds.includes(u._id));

    return (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr auto',
                    gap: 2,
                    alignItems: 'flex-start',
                }}
            >
                {/* OrgMultiSelect */}
                <Autocomplete<Organization, true>
                    multiple
                    options={orgs}
                    loading={orgsLoading}
                    disabled={!isGlobalAdmin}
                    value={selectedOrgObjects}
                    onChange={handleOrgChange}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    sx={{ width: '100%' }}
                    renderTags={(value, getTagProps) =>
                        value.map((o, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={o._id}
                                label={o.name}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Organisations"
                            size="small"
                        />
                    )}
                />

                {/* UserMultiSelect — chips show name only, dropdown shows name + email */}
                <Autocomplete<User, true>
                    multiple
                    options={users}
                    loading={usersLoading}
                    value={selectedUserObjects}
                    onChange={handleUserChange}
                    getOptionLabel={(u) => `${u.firstName} ${u.lastName} (${u.email})`}
                    isOptionEqualToValue={(u, v) => u._id === v._id}
                    sx={{ width: '100%' }}
                    renderTags={(value, getTagProps) =>
                        value.map((u, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={u._id}
                                label={`${u.firstName} ${u.lastName}`}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Users"
                            size="small"
                        />
                    )}
                />

                {/* DateRangePicker */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                        value={dateRange}
                        onChange={(newRange) => {
                            if (newRange[0] && newRange[1]) {
                                setDateRange([newRange[0], newRange[1]]);
                            }
                        }}
                        slots={{ field: SingleInputDateRangeField }}
                        slotProps={{
                            textField: { size: 'small', sx: { width: '100%' } },
                        }}
                        sx={{ width: '100%' }}
                    />
                </LocalizationProvider>

                {/* Export CSV Button */}
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCsv}
                    disabled={exportingCsv}
                    sx={{ whiteSpace: 'nowrap', height: 40, mt: 0.5 }}
                >
                    {exportingCsv ? 'Exporting...' : 'Export CSV'}
                </Button>
            </Box>
        </Paper>
    );
};

export default GlobalFilterBar;
